import { cloneElement, type ReactElement, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiDotsHorizontal } from '@mdi/js'
import { Button } from '../ui/button'
import { AppIcon } from '../ui/icon'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu'
import { cn } from '../../lib/utils'

/**
 * The « more actions » menu of a detail-page header (patterns §33).
 *
 * A header with one thing a user comes to the page to do keeps that thing as
 * its one visible primary button, and everything else — create a child, set a
 * parent, merge, settings, delete — goes behind this outline `…` trigger. Seven
 * outline buttons side by side say nothing about which one matters, and they do
 * not fit on a phone.
 *
 * Render nothing rather than an empty menu: a caller that has filtered every
 * item out (permissions) passes `hidden`.
 */
export function OverflowMenu({
    children,
    testId,
    label,
    hidden = false,
    size = 'default',
}: {
    children: ReactNode
    testId?: string
    /** Overrides « More actions » (aria-label and tooltip). */
    label?: string
    hidden?: boolean
    /** `default` in a page header, next to its buttons; `sm` in a table row. */
    size?: 'default' | 'sm'
}) {
    const { t } = useTranslation('common')
    if (hidden) return null
    const text = label ?? t('actions.more_actions')

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className={size === 'sm' ? 'h-8 w-8' : undefined} aria-label={text} title={text} data-testid={testId}>
                    <AppIcon path={mdiDotsHorizontal} size="sm" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-48 max-w-72">
                {children}
            </DropdownMenuContent>
        </DropdownMenu>
    )
}

/**
 * One entry of an {@link OverflowMenu}: an icon and a label, optionally a hint.
 *
 * - `onSelect` runs one tick after the menu closes. An item that opens a
 *   dialog would otherwise open it while Radix is still unmounting the menu,
 *   which leaves `pointer-events: none` on the body and an unclickable dialog.
 * - `children`, when given, is the element the item renders AS — a router
 *   `Link`, so the entry stays a real link (middle-click, open in a new tab).
 *   Its own children are replaced by the icon and the label.
 * - `hint` is a muted line under the label. A disabled item says why it is
 *   disabled there: a `title` tooltip never shows on it, the item has
 *   `pointer-events: none`.
 * - `destructive` is the red Delete tone; it goes last, after a separator.
 */
export function OverflowMenuItem({
    icon,
    label,
    hint,
    destructive = false,
    disabled = false,
    onSelect,
    testId,
    children,
}: {
    icon: string
    label: ReactNode
    hint?: ReactNode
    destructive?: boolean
    disabled?: boolean
    onSelect?: () => void
    testId?: string
    children?: ReactElement<{ children?: ReactNode }>
}) {
    const body = (
        <>
            <AppIcon path={icon} size="sm" className={cn('mr-2 self-start mt-0.5 flex-shrink-0', destructive && 'text-destructive')} />
            <span className="min-w-0">
                <span className="block">{label}</span>
                {hint && (
                    <span className="block text-xs text-muted-foreground" data-testid={testId ? `${testId}-hint` : undefined}>
                        {hint}
                    </span>
                )}
            </span>
        </>
    )
    const className = cn(destructive && 'text-destructive focus:text-destructive')

    if (children) {
        return (
            <DropdownMenuItem asChild disabled={disabled} className={className} data-testid={testId}>
                {cloneElement(children, undefined, body)}
            </DropdownMenuItem>
        )
    }

    return (
        <DropdownMenuItem
            disabled={disabled}
            className={className}
            onSelect={() => {
                if (onSelect) setTimeout(onSelect, 0)
            }}
            data-testid={testId}
        >
            {body}
        </DropdownMenuItem>
    )
}

/** The rule above the destructive entry. */
export const OverflowMenuSeparator = DropdownMenuSeparator
